import React, { useMemo } from 'react';

import TimeIcon from '@mui/icons-material/AccessTime';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import EmailOutlinedIcon from '@mui/icons-material/EmailOutlined';
import LocationIcon from '@mui/icons-material/FmdGood';
import MeetingRoomIcon from '@mui/icons-material/MeetingRoom';
import PhoneOutlinedIcon from '@mui/icons-material/PhoneOutlined';
import { Box, SxProps, Typography } from '@mui/material';

import IconList from '~/components/v2/common/IconList/IconList';
import { ReadOnlyFieldType } from '~/components/v2/common/ReadOnlyField/types';

import { getCurrentTimeInTimezone } from '~/services/TimeService';

import OpportunityPropertyInfoSection from './OpportunityPropertyInfoSection';

const SIDEBAR_STYLE: SxProps = {
  height: '100%',
  display: 'flex',
  flexDirection: 'column',
  overflow: 'hidden',
  '& > *': {
    flex: '0 0 auto',
  },
  '& > *:last-child': {
    flex: '1 1 auto',
    overflow: 'auto',
    '&::-webkit-scrollbar': {
      display: 'none',
    },
    msOverflowStyle: 'none',
    scrollbarWidth: 'none',
  },
};

interface OpportunityPropertyInfoProps {
  account: App.OfferAccount;
  usp: string;
  location: App.Location;
  keyContacts: App.KeyContacts;
  property: App.Property;
}

export default function OpportunityPropertyInfo({
  account,
  usp,
  location,
  keyContacts,
  property,
}: OpportunityPropertyInfoProps) {
  const {
    wi_fi__c,
    parking__c,
    smoking_policy__c,
    disability_access__c,
    kids_club__c,
    babysitting__c,
    year_opened__c,
    currencyisocode,
    hotel_last_renovation__c,
    closest_airport__c,
    airport_transfer_cost__c,
    location_attractions__c,
    local_attractions_shuttle__c,
    merchant_time_zone__c,
  } = account || {};

  const { destination__c, sub_destination__c } = location || {};

  const { booking_email__c, reservations_phone__c } = keyContacts || {};

  const sections = useMemo(
    () => [
      {
        title: 'Unique Selling Point',
        fields: [
          {
            title: 'USP',
            value: usp || '-',
            type: 'text' as ReadOnlyFieldType,
          },
        ],
      },
      {
        title: "What's Around",
        fields: [
          { title: 'Closest Airport', value: closest_airport__c || '-', type: 'text' as ReadOnlyFieldType },
          {
            title: 'Airport Transfer Cost',
            value: airport_transfer_cost__c || '-',
            type: 'text' as ReadOnlyFieldType,
          },
          {
            title: 'Local Attractions',
            value: location_attractions__c || '-',
            type: 'text' as ReadOnlyFieldType,
          },
          {
            title: 'Local Attractions Shuttle',
            value: local_attractions_shuttle__c || '-',
            type: 'text' as ReadOnlyFieldType,
          },
        ],
      },
      {
        title: 'Amenities & Services',
        fields: [
          { title: 'Wi-fi', value: wi_fi__c || '-', type: 'text' as ReadOnlyFieldType },
          { title: 'Parking', value: parking__c || '-', type: 'text' as ReadOnlyFieldType },
          {
            title: 'Smoking Policy',
            value: smoking_policy__c || '-',
            type: 'text' as ReadOnlyFieldType,
          },
          {
            title: 'Disability Access',
            value: disability_access__c || '-',
            type: 'text' as ReadOnlyFieldType,
          },
          { title: 'Kids Club', value: kids_club__c || '-', type: 'text' as ReadOnlyFieldType },
          { title: 'Babysitting', value: babysitting__c || '-', type: 'text' as ReadOnlyFieldType },
        ],
      },
      {
        title: 'Highlights',
        fields: [
          {
            title: 'Recent Awards/Accolades',
            value: account?.recent_awards_accolades__c || '-',
            type: 'text' as ReadOnlyFieldType,
          },
          { title: 'Year Opened', value: year_opened__c || '-', type: 'text' as ReadOnlyFieldType },
          { title: 'Last Renovated', value: hotel_last_renovation__c || '-', type: 'text' as ReadOnlyFieldType },
        ],
      },
    ],
    [
      usp,
      closest_airport__c,
      airport_transfer_cost__c,
      location_attractions__c,
      local_attractions_shuttle__c,
      wi_fi__c,
      parking__c,
      smoking_policy__c,
      disability_access__c,
      kids_club__c,
      babysitting__c,
      year_opened__c,
      hotel_last_renovation__c,
      account?.recent_awards_accolades__c,
    ],
  );

  return (
    <Box sx={SIDEBAR_STYLE}>
      <Box
        sx={{
          py: { md: 0.5 },
        }}
      >
        <Typography variant="overline">Property Info</Typography>
        <Typography variant="h6">Property Name</Typography>
      </Box>

      <Box>
        <IconList
          items={[
            {
              value: account?.check_in_out_time__c || '-',
              icon: <MeetingRoomIcon />,
              tooltipText: 'Check in/out Time',
            },
            {
              value: currencyisocode || '-',
              icon: <AttachMoneyIcon />,
              tooltipText: 'Local Currency',
            },
            {
              value: merchant_time_zone__c ? getCurrentTimeInTimezone(merchant_time_zone__c) : '-',
              icon: <TimeIcon />,
              tooltipText: 'Merchant Time Zone',
            },
            {
              value: booking_email__c || '-',
              icon: <EmailOutlinedIcon />,
              tooltipText: 'Reservation Email',
              copyable: true,
              title: 'Reservation Email',
            },
            {
              value: reservations_phone__c || '-',
              icon: <PhoneOutlinedIcon />,
              tooltipText: 'Reservation Phone',
              copyable: true,
              copyableType: 'number',
              title: 'Reservation Phone',
            },
            {
              value:
                sub_destination__c && destination__c
                  ? `${sub_destination__c}, ${destination__c}`
                  : sub_destination__c
                  ? `${sub_destination__c}`
                  : destination__c
                  ? `${destination__c}`
                  : '-',
              icon: <LocationIcon />,
              tooltipText: 'Location',
              href:
                property?.latitude && property?.longitude && (sub_destination__c || destination__c)
                  ? `https://www.google.com/maps/search/?api=1&query=${property.latitude},${property.longitude}`
                  : undefined,
            },
          ]}
        />
      </Box>

      {sections.map((section, index) => (
        <OpportunityPropertyInfoSection key={index} title={section.title} fields={section.fields} />
      ))}
    </Box>
  );
}
